// Server-room arena (portrait). Tiled floor, decorative props, and a thin
// Main Blue boundary wall. Server racks and desktop workstations are the
// hotspots — they get static physics bodies so CVEs bounce off them and the
// player can't walk through them. AssetSystem owns the per-hotspot asset
// lifecycle (markers, exposed/exploiting/patched state).

import Phaser from 'phaser';
import { brandHex } from '../brand';
import { config } from '../config';
import { SPRITE_KEYS } from './sprites';
import { makePropLabel } from './callout';

export interface SearchHotspot {
  x: number;
  y: number;
  kind: 'rack' | 'desk';
  /** The visible decor image — AssetSystem tints this when exploiting/exploited. */
  body: Phaser.GameObjects.Image;
}

export interface Arena {
  bounds: Phaser.Geom.Rectangle;
  walls: Phaser.Physics.Arcade.StaticGroup;
  /** Racks + desks. CVEs bounce off these; AssetSystem looks up the hotspot
   *  via item.getData('hotspot') on collision. */
  items: Phaser.Physics.Arcade.StaticGroup;
  decor: Phaser.GameObjects.GameObject[];
  hotspots: SearchHotspot[];
  /** The drop-off point. AssetSystem auto-patches a carried asset when the
   *  player reaches this position. */
  scanner: { x: number; y: number };
}

export interface BuildArenaOptions {
  /** Skip placing the scanner sprite + label. Used by BossScene where
   *  scanning isn't part of the encounter. */
  withScanner?: boolean;
}

export const buildArena = (scene: Phaser.Scene, opts: BuildArenaOptions = {}): Arena => {
  const withScanner = opts.withScanner !== false;
  const W = config.arena.width;
  const H = config.arena.height;
  const HUD_H = config.hud.height;

  // Floor
  scene.add.tileSprite(0, HUD_H, W, H - HUD_H, SPRITE_KEYS.floor).setOrigin(0, 0).setDepth(0);

  const walls = scene.physics.add.staticGroup();

  // Thin Main Blue walls on all four sides of the arena (below the HUD).
  // Single rectangles per side — a 4px line, matching the IONIX reference.
  const WALL = 4;
  const wallTop = HUD_H;
  const wallBottom = H - WALL;
  const sideY = wallTop + WALL + (wallBottom - wallTop - WALL) / 2;
  const sideH = wallBottom - wallTop - WALL;

  const addWall = (cx: number, cy: number, w: number, h: number) => {
    const r = scene.add.rectangle(cx, cy, w, h, brandHex.primary).setDepth(1);
    scene.physics.add.existing(r, true);
    walls.add(r);
  };
  addWall(W / 2, wallTop + WALL / 2, W, WALL);                 // top
  addWall(W / 2, wallBottom + WALL / 2, W, WALL);              // bottom
  addWall(WALL / 2, sideY, WALL, sideH);                        // left
  addWall(W - WALL / 2, sideY, WALL, sideH);                    // right

  const innerX = 32;
  const innerY = wallTop + 32;
  const innerW = W - 64;
  const innerH = wallBottom - innerY;

  const bounds = new Phaser.Geom.Rectangle(innerX, innerY, innerW, innerH);

  const decor: Phaser.GameObjects.GameObject[] = [];
  const hotspots: SearchHotspot[] = [];
  const items = scene.physics.add.staticGroup();

  const addItem = (img: Phaser.GameObjects.Image, hotspot: SearchHotspot) => {
    scene.physics.add.existing(img, true);
    items.add(img);
    img.setData('hotspot', hotspot);
  };

  // ----- Server racks (top wall) — primary search targets ------------------
  // Rack sprite is 32×48; origin (0,0). Hotspot sits at the rack's centre.
  // Each rack uses a random visual variant for visual variety.
  const RACK_VARIANTS = [
    SPRITE_KEYS.serverRack,
    SPRITE_KEYS.serverRackB,
    SPRITE_KEYS.serverRackC,
    SPRITE_KEYS.serverRackD,
  ];
  const rackY = innerY + 6;
  for (let i = 0; i < 4; i++) {
    const rx = innerX + 20 + i * 120;
    const variant = RACK_VARIANTS[Phaser.Math.Between(0, RACK_VARIANTS.length - 1)];
    const rack = scene.add.image(rx, rackY, variant).setOrigin(0, 0).setDepth(2);
    const hx = rx + 16;        // horizontal centre of 32 px-wide rack
    const hy = rackY + 24;     // vertical centre of 48 px-tall rack
    const hotspot: SearchHotspot = { x: hx, y: hy, kind: 'rack', body: rack };
    addItem(rack, hotspot);
    hotspots.push(hotspot);
  }

  // ----- Workstations along the side walls — secondary search targets -------
  // Desktop sprite is 32×32; origin (0,0). Hotspot sits above the monitor.
  // Skip the bottom row (would land in the joystick / ACT button zones on
  // mobile — the player can't tap something they can't see).
  const deskOffsets = [innerH * 0.32, innerH * 0.55];
  deskOffsets.forEach((dy) => {
    const yL = innerY + dy - 16;

    // left workstation
    const ld = scene.add.image(innerX + 6, yL, SPRITE_KEYS.desktop).setOrigin(0, 0).setDepth(3);
    decor.push(scene.add.image(innerX + 10, yL + 32, SPRITE_KEYS.chair).setOrigin(0, 0).setDepth(2));
    const lhx = innerX + 6 + 16;
    const lhotspot: SearchHotspot = { x: lhx, y: yL + 16, kind: 'desk', body: ld };
    addItem(ld, lhotspot);
    hotspots.push(lhotspot);

    // right workstation
    const rd = scene.add.image(innerX + innerW - 38, yL, SPRITE_KEYS.desktop).setOrigin(0, 0).setDepth(3);
    decor.push(scene.add.image(innerX + innerW - 34, yL + 32, SPRITE_KEYS.chair).setOrigin(0, 0).setDepth(2));
    const rhx = innerX + innerW - 38 + 16;
    const rhotspot: SearchHotspot = { x: rhx, y: yL + 16, kind: 'desk', body: rd };
    addItem(rd, rhotspot);
    hotspots.push(rhotspot);
  });

  // ----- Scanner — drop-off point for carried assets ---------------------
  const scannerX = innerX + innerW / 2;
  const scannerY = innerY + innerH / 2;
  if (withScanner) {
    decor.push(scene.add.image(scannerX, scannerY, SPRITE_KEYS.scanner).setOrigin(0.5, 0.5).setDepth(3));
    decor.push(makePropLabel(scene, scannerX, scannerY + 36, 'SCANNER'));
  }

  // ----- Scattered decor — variety pack, randomised positions -------------
  // Placement avoids the bottom-left + bottom-right corner zones (those are
  // covered by the on-screen joystick and ACT button on touch devices).
  // Pool used for repeatable picks. Coffee is excluded (capped at 1, placed
  // separately) and floor_mat is dropped entirely. Door, switch, cable add
  // server-room flavour.
  const DECOR_POOL = [
    SPRITE_KEYS.crate,
    SPRITE_KEYS.crate,        // weight crates a bit so they still feel dominant
    SPRITE_KEYS.printer,
    SPRITE_KEYS.locker,
    SPRITE_KEYS.whiteboard,
    SPRITE_KEYS.trash,
    SPRITE_KEYS.monitorWall,
    SPRITE_KEYS.plant,
    SPRITE_KEYS.door,
    SPRITE_KEYS.switch,
    SPRITE_KEYS.cable,
  ];
  // Sprites that should appear at most once per arena.
  const SINGLETON_DECOR = [SPRITE_KEYS.coffee];
  const DECOR_COUNT = 10;
  const MIN_DIST = 64;
  const PLAYER_X = innerX + 80;
  const PLAYER_Y = innerY + innerH / 2;

  const interiorMinX = innerX + 48;
  const interiorMaxX = innerX + innerW - 48;
  const interiorMinY = innerY + 90; // clear the top rack row
  const interiorMaxY = innerY + innerH - 32;

  // Bottom-left + bottom-right corners are where the joystick / ACT button
  // sit on touch — keep decor out of them so the player can see what they're
  // pressing on. Sized in arena coords (canvas is 540×960 native).
  const CORNER_W = 170;
  const CORNER_H = 170;
  const inLeftCorner = (x: number, y: number) =>
    x < innerX + CORNER_W && y > innerY + innerH - CORNER_H;
  const inRightCorner = (x: number, y: number) =>
    x > innerX + innerW - CORNER_W && y > innerY + innerH - CORNER_H;

  const placed: Array<{ x: number; y: number }> = [];
  let safety = 0;
  while (placed.length < DECOR_COUNT && safety < 400) {
    safety++;
    const x = Phaser.Math.Between(interiorMinX, interiorMaxX);
    const y = Phaser.Math.Between(interiorMinY, interiorMaxY);
    if (inLeftCorner(x, y) || inRightCorner(x, y)) continue;
    if (Phaser.Math.Distance.Between(x, y, scannerX, scannerY) < 90) continue;
    if (Phaser.Math.Distance.Between(x, y, PLAYER_X, PLAYER_Y) < 70) continue;
    if (placed.some((p) => Phaser.Math.Distance.Between(x, y, p.x, p.y) < MIN_DIST)) continue;
    placed.push({ x, y });
  }
  // Place singletons first (e.g. exactly one coffee), then fill the rest from
  // the repeatable pool.
  placed.forEach((p, i) => {
    const key = i < SINGLETON_DECOR.length
      ? SINGLETON_DECOR[i]
      : DECOR_POOL[Phaser.Math.Between(0, DECOR_POOL.length - 1)];
    decor.push(scene.add.image(p.x, p.y, key).setOrigin(0.5).setDepth(3));
  });

  return { bounds, walls, items, decor, hotspots, scanner: { x: scannerX, y: scannerY } };
};
